/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


 function delayAnimation() {
    let x = window.screen.width;

    function check_price_size() {
        const product_price_block = Array.from(document.querySelectorAll(".product-block .block-inner .product-price"));
        const product_title_block = Array.from(document.querySelectorAll(".product-block .block-inner .product-block__title"));
    
        let bigger_price = 0;
        let bigger_price_string;
    
        product_price_block.forEach((price_inner) => {
            price_inner.style.height = "auto";
            if (price_inner.offsetHeight > bigger_price) {
                bigger_price = price_inner.offsetHeight;
            }
        });
    
        bigger_price_string = bigger_price.toString() + 'px';
    
        product_title_block.forEach((block_title) => {
            // block_title.style.paddingBottom = bigger_price_string;
        });
    }
    
    function check_inner_size() {
        const array_product_block = Array.from(document.querySelectorAll(".product-block .block-inner .block-inner-inner"));
        let bigger_block = 0;
        let bigger_block_string;
    
        array_product_block.forEach((block_inner) => {
            block_inner.style.height = "auto";
            if (block_inner.offsetHeight > bigger_block) {
                bigger_block = block_inner.offsetHeight;
            }
        });
    
        bigger_block_string = bigger_block.toString() + 'px';
    
        array_product_block.forEach((block_inner) => {
            block_inner.style.height = bigger_block_string;
        });
    }
    
    document.addEventListener('theme:ajax:done', () => {
        check_price_size();
        check_inner_size();
    });

    window.addEventListener('resize', function() {
        x = window.screen.width;
        check_price_size();
        check_inner_size();
    });
    
    check_price_size();
    check_inner_size();
}

// Initialize the animation function
delayAnimation();

// Wait for window load event and then run with delay
window.addEventListener('load', function() {
    setTimeout(delayAnimation, 1000);
});

document.addEventListener('click', function (e) {
    const header = e.target.closest('.filter-menu-header');
  
    if (header) {
      const isActive = header.classList.contains('active');
  
      // Remove active from all headers and items
      document.querySelectorAll('.filter-menu-header').forEach(h => h.classList.remove('active'));
      document.querySelectorAll('.filter-menu-items').forEach(i => i.classList.remove('active'));
  
      if (!isActive) {
        const group = header.closest('.filter__menu-group');
        const items = group.querySelector('.filter-menu-items');
        header.classList.add('active');
        items.classList.add('active');
      }
    }
  });

/**
 * Update robots tag dynamically when pagination changes
 * This is necessary because AJAX navigation doesn't reload the <head>
 */
(function() {
  'use strict';

  function isPaginationPage() {
    const path = window.location.pathname;
    
    if (path.includes('/blogs/')) {
      return false;
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    
    return pageParam && parseInt(pageParam) > 1;
  }

  function getTemplateName() {
    const body = document.body;
    const classes = body.className.split(' ');
    const templateClass = classes.find(cls => cls.startsWith('template-'));
    
    if (templateClass) {
      return templateClass.replace('template-', '');
    }
    
    const path = window.location.pathname;
    if (path.includes('/collections/')) return 'collection';
    if (path.includes('/search')) return 'search';
    if (path.includes('/blogs/')) return 'blog';
    if (path.includes('/products/')) return 'product';
    
    return 'index';
  }

  function updateRobotsTag() {
    const templateName = getTemplateName();
    const isPagination = isPaginationPage();
    
    let robotsTag = document.querySelector('meta[name="robots"]');
    
    if (!robotsTag) {
      robotsTag = document.createElement('meta');
      robotsTag.setAttribute('name', 'robots');
      document.head.appendChild(robotsTag);
    }
    
    let robotsContent = 'INDEX,FOLLOW';
    
    if (isPagination && templateName !== 'blog') {
      robotsContent = 'NOINDEX,FOLLOW';
    } else {
      robotsContent = 'INDEX,FOLLOW';
    }
    
    robotsTag.setAttribute('content', robotsContent);
  }

  document.addEventListener('theme:ajax:done', function() {
    setTimeout(updateRobotsTag, 100);
  });

  window.addEventListener('popstate', function() {
    setTimeout(updateRobotsTag, 100);
  });

  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function() {
    originalPushState.apply(history, arguments);
    setTimeout(updateRobotsTag, 100);
  };

  history.replaceState = function() {
    originalReplaceState.apply(history, arguments);
    setTimeout(updateRobotsTag, 100);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateRobotsTag);
  } else {
    updateRobotsTag();
  }
})();
  
  
  